import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./style/confirmation.scss";
import checkGif from "../../assets/images/check.gif";
import { Card } from "react-bootstrap";
import {
  touchlessService,
  type VisitPayload,
} from "../../services/api/touchlessService";
import { setCookie, formatScheduledDate } from "../../utils/helpers";
import LoadingSpinner from "../../components/ui/Spinner/LoadingSpinner";
import { showError } from "../../services/toastservice/toastService";
import { useAppConfig } from "../../hooks/useAppConfig";
import { useVisitorData } from "../../hooks/useVisitorData";
import { envService } from "../../services/api/envService";
import { ROUTES } from "../../utils/constants";
import { useStrings } from "../../hooks/useStrings";

interface VisitorInfo extends VisitPayload {
  id?: string;
  preRegisterVisitId?: string;
  visitorTypeId?: string;
  videoWatched?: boolean;
}

const Confirmation: React.FC = () => {
  const S = useStrings();
  const { config: orgConfig, themeColors } = useAppConfig();
  const {
    visitorinfo,
    visitorTypeId,
    preregisterId,
    preFillNew,
    isPrefill,
    preFillData,
    repeatCheckin,
    watchlistMessage,
    welcomeMessage,
    visitStatus,
    updateVisitStatus,
    updateVisitorInfo,
  } = useVisitorData();
  const primaryColor = themeColors.btnColor;
  const navigate = useNavigate();

  const [confirm, setConfirm] = useState<boolean>(false);
  const [isStatusReview, setIsStatusReview] = useState<boolean>(
    preFillData?.visitStatus === "IN_REVIEW" ? true : false,
  );
  const [loading, setLoadingState] = useState<boolean>(false);
  // const [hasError, setHasError] = useState<boolean>(false);
  const hasSubmittedRef = useRef<boolean>(false);

  useEffect(() => {
    if (!confirm) return;
    const handlePopState = () => {
      navigate(`/${ROUTES.CONFIRMATION}`, { replace: true });
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [confirm, navigate]);

  useEffect(() => {
    if (hasSubmittedRef.current) return;
    hasSubmittedRef.current = true;

    if (visitStatus?.visitId) {
      console.log(
        `Confirmation page: Visit aleady created with id:${visitStatus.visitId}. So restricting user from creating duplicate`,
      );
      setCookie("visitId", visitStatus.visitId, 2 / 3);
      setConfirm(true);
      return;
    }
    if (!visitorinfo) {
      navigate("/");
      return;
    }

    submitVisit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitVisit = async () => {
    setLoadingState(true);
    try {
      if (orgConfig && !touchlessService.isNullUndefinedOrBlank(orgConfig)) {
        setCookie("siteName", orgConfig.siteName, 2 / 3);
        setCookie("orgName", orgConfig.orgName, 2 / 3);
      }
      const visitorInfoData = {
        ...(visitorinfo as VisitorInfo),
        visitorTypeId: visitorTypeId,
      };
      updateVisitorInfo(visitorInfoData);

      if (!visitorInfoData) {
        setLoadingState(false);
        console.error("confirmation page: Visitor information empty");
        return;
      } else {
        // Set check-in time and other properties
        setCookie(
          "signInDateTime",
          touchlessService.formatDate("DD MMM YY hh:mm A"),
          2 / 3,
        );
        if (visitorInfoData?.fullName) {
          setCookie("visitorName", String(visitorInfoData.fullName), 2 / 3);
        }
        if (visitorTypeId) {
          setCookie("visitorTypeId", visitorTypeId, 2 / 3);
        }
        if (visitorInfoData?.hostName) {
          setCookie("hostName", String(visitorInfoData.hostName), 2 / 3);
        }

        const smartpassLat = sessionStorage.getItem("smartpassLat");
        const smartpassLng = sessionStorage.getItem("smartpassLng");

        const payload: VisitPayload = {
          ...(visitorInfoData as VisitPayload),
          checkInTime: touchlessService.formatDate("YYYY-MM-DDTHH:mm:ss") || "",
          orgId: orgConfig?.orgId,
          siteId: orgConfig?.siteId,
          locationId: orgConfig?.locationId,
          deviceId: orgConfig?.deviceId,
          checkinMethod: sessionStorage.getItem("smartpassUuid") ? "SMARTPASS" : "WEBMOBILE",
          smartpassPassId: sessionStorage.getItem("smartpassUuid") || undefined,
          checkinLatitude: smartpassLat ? parseFloat(smartpassLat) : undefined,
          checkinLongitude: smartpassLng ? parseFloat(smartpassLng) : undefined,
          scheduledCheckInTime: "",
        };
        if (preregisterId) payload.preRegisterVisitId = preregisterId;

        // EN-4557 cycle 2+: link this visit to the previous cycle so the server carries the
        // photo, the signed documents, the video stamp and any still-valid ID forward, and the
        // visitor never had to supply them again. Only when the source visit belongs to THIS
        // site: resolveRootReferenceVisitId rejects a cross-site reference with a bare 400
        // before the friendlier multi check-in refusal can be produced. Carry-forward still
        // resolves by identity without it, so the guard costs nothing.
        if (repeatCheckin) {
          delete payload.id;
          payload.preRegisterVisitId =
            repeatCheckin.preRegisterVisitId || preregisterId || undefined;
          if (
            !repeatCheckin.sourceSiteId ||
            repeatCheckin.sourceSiteId === orgConfig?.siteId
          ) {
            payload.referenceVisitId = repeatCheckin.sourceVisitId;
          }
        }
        updateVisitorInfo(payload);

        if (!isPrefill) {
          // Contactless ?id= flow where visitInfoModel was found in preregistration data:
          // preFillNew + preFillData are set by visitorForm.tsx in this case.
          // Must patch the existing visit (not POST a new one) to avoid 500.
          if (
            // EN-4557: a repeat cycle must never PATCH -- the server refuses a PATCH against a
            // completed cycle with 409 cycle_already_completed. Falls through to the POST below.
            !repeatCheckin &&
            preFillNew &&
            !touchlessService.isNullUndefinedOrBlank(preFillData)
          ) {
            const prefillDetails = preFillData as {
              id: string;
              [key: string]: unknown;
            };
            const param = {
              visitStatus: "DRAFT",
              preRegisterVisitId:
                (visitorInfoData as VisitorInfo).id || preregisterId,
              checkinTime: touchlessService.formatDate("YYYY-MM-DDTHH:mm:ss"),
            };
            touchlessService
              .patchVisit(param, prefillDetails)
              .then(() => {
                setConfirm(true);
                setCookie("visitId", prefillDetails.id, 2 / 3);
                updateVisitStatus({
                  status: "DRAFT",
                  visitId: prefillDetails?.id,
                  message: null,
                });
              })
              .catch((error) => {
                console.debug("Error patching visit (contactless):", error);
                // 401 and network errors are already toasted by the interceptor.
                const status = (error as { response?: { status?: number } })?.response?.status;
                const hasServerResponse = !!(error as { response?: unknown })?.response;
                if (hasServerResponse && status !== 401) {
                  showError("Error encountered while updating visit. Please try again.");
                }
                // setHasError(true);
              });
            return;
          }

          const preregisterStr = sessionStorage.getItem("preregister");
          const preregister = preregisterStr
            ? JSON.parse(preregisterStr)
            : null;

          if (touchlessService.isNullUndefinedOrBlank(preregister)) {
            // Post new visit
            const response = await touchlessService.postVisit(payload);
            updateVisitStatus({
              ...response,
              status: response?.visitStatus,
              visitId: response?.id,
              message: null,
            });
            if (response && response.visitStatus === "IN_REVIEW") {
              setIsStatusReview(true);
            } else {
              setIsStatusReview(false);
            }

            setConfirm(true);
            setCookie("visitId", response.id, 2 / 3);

            // If this was a SmartPass check-in, update the pass status
            // so Apple Wallet shows "CHECKED IN" on the next poll
            const smartpassUuid = sessionStorage.getItem("smartpassUuid");
            if (smartpassUuid && response.id) {
              try {
                await fetch(
                  `${envService.apiUrl}/smartpass/checkin/${smartpassUuid}/complete`,
                  {
                    method: "POST",
                    headers: {
                      "Content-Type": "application/json",
                      Authorization: sessionStorage.getItem("accessToken") || "",
                    },
                    body: JSON.stringify({ visitId: response.id }),
                  }
                );
                sessionStorage.removeItem("smartpassUuid");
                sessionStorage.removeItem("smartpassLat");
                sessionStorage.removeItem("smartpassLng");
              } catch (e) {
                console.error("Failed to update SmartPass status:", e);
              }
            }
          } else {
            setConfirm(true);
          }
        } else {
          if (preFillNew) {
            // Update existing prefilled visit
            const prefillDetails = preFillData
              ? (preFillData as { id: string; [key: string]: unknown })
              : { id: "" };

            const param = {
              visitStatus: "DRAFT",
              preRegisterVisitId: visitorInfoData.id || preregisterId,
              checkinTime: touchlessService.formatDate("YYYY-MM-DDTHH:mm:ss"),
            };

            touchlessService
              .patchVisit(param, prefillDetails)
              .then(() => {
                setTimeout(() => {
                  setConfirm(true);
                  setCookie("visitId", prefillDetails.id, 2 / 3);
                  updateVisitStatus({
                    status: "DRAFT",
                    visitId: prefillDetails?.id,
                    message: null,
                  });
                }, 5000);
              })
              .catch((error) => {
                console.debug("Error prefill visit creation:", error);
                showError(S.CONFIRMATION.TOAST_UPDATE_VISIT_ERROR);
              });
          } else {
            const prefillDetails = preFillData as {
              id: string;
              siteId: string;
              [key: string]: unknown;
            };
            const tempVisitorInfo = { ...(visitorInfoData as VisitPayload) };
            delete tempVisitorInfo.id;
            tempVisitorInfo.preRegisterVisitId = prefillDetails?.id;
            tempVisitorInfo.siteId = prefillDetails?.siteId;
            updateVisitorInfo(tempVisitorInfo);

            const response: { [key: string]: unknown } =
              await touchlessService.postPrefillVisit(tempVisitorInfo);
            updateVisitStatus({
              status: null,
              visitId: response?.id as string,
              message: null,
            });
            setConfirm(true);
          }
        }
      }
    } catch (error) {
      console.error("Submit visit error:", error);
      // EN-4557: a refused cycle answers 403/409/410 with a reason-coded, already user-facing
      // message (already_on_site, window_expired, cap_reached). Showing it beats a generic
      // toast, and scoping it to the repeat path leaves every other flow untouched.
      const serverMessage = (error as { response?: { data?: { message?: string } } })
        ?.response?.data?.message;
      if (repeatCheckin && serverMessage) {
        showError(serverMessage);
      } else {
        showError(S.CONFIRMATION.TOAST_GENERIC_ERROR);
      }
    } finally {
      setLoadingState(false);
      // dispatch(setLoading(false));
    }
  };

  if (loading) {
    return <LoadingSpinner fullScreen={true} />;
  }

  if (!confirm) {
    return (
      <>
        <LoadingSpinner fullScreen={true} />
        {/* <p className="error">Error please try again...</p> */}
      </>
    );
  }

  return (
    <div
      className="main-screen scrollable"
      data-testid="confirmation-main-screen"
    >
      <div
        className="screen-outer screen-confirmation"
        id="confirmation-page"
        data-testid="confirmation-screen-outer"
      >
        <div className="screen-inner" data-testid="confirmation-screen-inner">
          <div
            className="screen-subinner"
            data-testid="confirmation-screen-subinner"
          >
            {!isPrefill && (
              <Card
                className="card card-confirm"
                data-testid="confirmation-card-welcome"
              >
                <Card.Body
                  className="card-body"
                  data-testid="confirmation-card-body-welcome"
                >
                  <div
                    className="confirm-icon"
                    data-testid="confirmation-confirm-status"
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <img
                        src={checkGif}
                        alt="checkmark"
                        style={{ height: 170, maxWidth: "none" }}
                      />
                    </div>
                  </div>

                  <div
                    className="confirm-content"
                    data-testid="confirmation-content-welcome"
                  >
                    <h2
                      style={{ color: themeColors.textColor }}
                      data-testid="confirmation-welcome-title"
                    >
                      {S.CONFIRMATION.CHECKED_IN_TITLE}
                    </h2>
                    {!!visitorinfo?.hostName && (
                      <p data-testid="confirmation-welcome-name" hidden={true}>
                        <strong>{visitorinfo.hostName as string}</strong> has
                        been notified.
                      </p>
                    )}
                    <p data-testid="confirmation-welcome-message">
                      {!isStatusReview
                        ? (welcomeMessage || S.CONFIRMATION.DEFAULT_WELCOME_MESSAGE)
                        : (watchlistMessage || S.CONFIRMATION.DEFAULT_WELCOME_MESSAGE)}
                    </p>
                  </div>

                  <div
                    className="confirm-wallets"
                    data-testid="confirmation-wallets"
                    hidden={true}
                  >
                    <div className="confirm-wallets-grid">
                      <button type="button" className="btn-wallet btn-wallet--apple">
                        <svg
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          width="22"
                          height="22"
                        >
                          <path d="M17.27 1.48C18.66.24 20.37 0 20.37 0s.13 1.83-1 3.22c-.9 1.12-2.39 1.89-2.39 1.89s-1.42.06-2.67-1.16c-1.12-1.09-.94-2.47-.94-2.47zm-5.11 3.26c1.69 0 2.64.91 3.32.91 1.04 0 2.51-1.3 4.19-1.21 1.63.08 2.87.89 3.59 2.06-3.32 1.83-2.6 5.67.62 6.94-.65 1.69-1.54 3.34-2.73 4.96-.8 1.1-1.63 2.19-2.88 2.21-1.28.02-1.69-.76-3.15-.76-1.5 0-1.92.74-3.15.78-1.26.04-2.22-1.26-2.98-2.38-1.61-2.3-2.84-6.52-1.18-9.42.82-1.42 2.28-2.32 3.87-2.35.97-.03 1.95.67 2.54.67.57 0 1.63-.82 2.37-.82z" />
                        </svg>
                        <span>{S.CONFIRMATION.BTN_APPLE_WALLET}</span>
                      </button>
                      <button type="button" className="btn-wallet btn-wallet--google">
                        <svg viewBox="0 0 24 24" width="22" height="22">
                          <path
                            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                            fill="#4285F4"
                          />
                          <path
                            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                            fill="#34A853"
                          />
                          <path
                            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.24.81-.6z"
                            fill="#FBBC05"
                          />
                          <path
                            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                            fill="#EA4335"
                          />
                        </svg>
                        <span>{S.CONFIRMATION.BTN_GOOGLE_WALLET}</span>
                      </button>
                    </div>
                    <p className="confirm-wallets-hint">
                      {S.CONFIRMATION.WALLET_HINT}
                    </p>
                  </div>
                </Card.Body>
              </Card>
            )}

            {isPrefill && (
              <div
                className="prefill-confirm-screen"
                data-testid="confirmation-prefill-screen"
              >
                {/* Success header */}
                <div className="prefill-confirm-header">
                  <div className="prefill-confirm-check-icon">
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <img
                        src={checkGif}
                        alt="checkmark"
                        style={{ height: 170, maxWidth: "none" }}
                      />
                    </div>
                  </div>
                  <h2 className="prefill-confirm-title">
                    {S.CONFIRMATION.REGISTRATION_COMPLETE_TITLE}
                  </h2>
                  <p className="prefill-confirm-subtitle">
                    {visitorinfo?.scheduleCheckinDate
                      ? `${S.CONFIRMATION.PREFILL_SCHEDULED_FOR.replace(
                          "{date}",
                          formatScheduledDate(
                            visitorinfo.scheduleCheckinDate as string,
                            ",",
                          ),
                        )} `
                      : ""}
                    {S.CONFIRMATION.PREFILL_HOST_NOTIFIED}
                  </p>
                </div>

                {/* Info card */}
                <div className="prefill-confirm-info-card">
                  <div className="prefill-confirm-info-row">
                    <i
                      className="bi bi-geo-alt prefill-confirm-info-icon"
                      style={{ color: primaryColor }}
                    />
                    <div>
                      <p className="prefill-confirm-info-title">
                        {visitorinfo?.siteName
                          ? visitorinfo?.siteName + ", "
                          : ""}
                        {orgConfig?.orgName}
                      </p>
                      {orgConfig?.siteName && (
                        <p className="prefill-confirm-info-sub">
                          {orgConfig.siteName}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="prefill-confirm-info-row">
                    <i
                      className="bi bi-clock prefill-confirm-info-icon"
                      style={{ color: primaryColor }}
                    />
                    <div>
                      <p className="prefill-confirm-info-title">
                        {S.CONFIRMATION.ARRIVAL_INSTRUCTIONS_TITLE}
                      </p>
                      <p className="prefill-confirm-info-sub">
                        {S.CONFIRMATION.ARRIVAL_INSTRUCTIONS_TEXT}
                      </p>
                    </div>
                  </div>
                </div>

                {/* QR code panel */}
                <div className="prefill-confirm-qr-panel" hidden={true}>
                  <div className="prefill-confirm-qr-placeholder">
                    <i className="bi bi-qr-code" />
                  </div>
                  <p className="prefill-confirm-qr-caption">
                    {S.CONFIRMATION.QR_CAPTION}
                  </p>
                  <button type="button" className="btn btn-outline-secondary prefill-confirm-qr-btn">
                    {S.CONFIRMATION.BTN_SAVE_QR}
                  </button>
                </div>

                {/* Action grid */}
                <div className="prefill-confirm-actions-grid" hidden={true}>
                  <button type="button" className="prefill-confirm-action-btn">
                    <div className="prefill-confirm-action-top">
                      <i className="bi bi-wallet2" />
                      <span>{S.CONFIRMATION.WALLET_LABEL}</span>
                    </div>
                    <span className="prefill-confirm-action-sub">{S.CONFIRMATION.ACTION_ADD_PASS}</span>
                  </button>
                  <button type="button" className="prefill-confirm-action-btn">
                    <div className="prefill-confirm-action-top">
                      <i className="bi bi-calendar-check" />
                      <span>{S.CONFIRMATION.CALENDAR_LABEL}</span>
                    </div>
                    <span className="prefill-confirm-action-sub">
                      {S.CONFIRMATION.ACTION_SAVE_EVENT}
                    </span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Confirmation;
